// Turno de la caja del taller (962): hook de estado y aviso «abrir turno» en
// línea para las pantallas que cobran (entrega, anticipo en efectivo, planes,
// usados), sin ir a otra pantalla.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { tallerRpc, QK, fieldCls, money, fmtFechaHora } from './shared';

export interface ResumenTurno {
  sesion_id: string; codigo: string; estado: string; fecha_apertura: string; fondo_inicial: number; n_ventas: number; total_ventas: number;
  efectivo_ventas: number; vuelto: number; ingresos: number; egresos: number; efectivo_sistema: number;
  formas: { nombre: string; tipo: string; total: number }[];
  movimientos: { tipo: string; monto: number; concepto: string | null; created_at: string }[];
  cajero?: string | null;
}
export interface TurnoEstado {
  activado: boolean;
  caja?: { id: string; codigo: string; nombre: string; serie: string };
  puede_operar?: boolean;
  cuentas_cierre?: { faltante_caja: boolean; sobrante_caja: boolean; ventas: boolean };
  turno?: ResumenTurno | null;
  denominaciones?: { id: string; valor: number; tipo: string }[];
  turnos?: { id: string; codigo: string; apertura: string; cierre: string | null; cajero: string | null; total_ventas: number | null;
             efectivo_sistema: number | null; efectivo_contado: number | null; diferencia: number | null; resolucion: string | null }[];
}

export function useTurnoTaller() {
  return useQuery<TurnoEstado>({ queryKey: [QK, 'turno'], queryFn: () => tallerRpc('turno_estado'), refetchInterval: 30000 });
}

/** Aviso del turno con apertura en línea. `motivo` explica por qué se necesita aquí. */
export function TurnoInline({ motivo = 'Para entregar y facturar se necesita el turno de la caja del taller abierto.' }: { motivo?: string }) {
  const qc = useQueryClient();
  const { data } = useTurnoTaller();
  const [fondo, setFondo] = useState('');
  const abrir = useMutation({
    mutationFn: () => tallerRpc<{ codigo: string }>('turno_abrir', { _fondo: Number(fondo) || 0 }),
    onSuccess: (r) => { toast.success(`Turno ${r.codigo} abierto`); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (!data) return null;
  if (!data.activado) {
    return <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="h-4 w-4 shrink-0" /> El taller no está activado: Configuración → Caja y cobros.</div>;
  }
  if (data.turno) {
    return <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
      <CheckCircle2 className="h-3.5 w-3.5" /> Turno {data.turno.codigo} abierto{data.turno.cajero ? ` por ${data.turno.cajero}` : ''} desde {fmtFechaHora(data.turno.fecha_apertura)}</div>;
  }
  return (
    <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
      <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" /> {motivo}</div>
      {data.puede_operar ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs">Fondo inicial de caja
            <input type="number" min={0} step="0.01" className={`${fieldCls} mt-0.5 h-9 w-32`} placeholder="0.00" value={fondo} onChange={(e) => setFondo(e.target.value)} />
          </label>
          <Button size="sm" isLoading={abrir.isPending} onClick={() => abrir.mutate()}>Abrir turno aquí</Button>
          {fondo && <span className="text-xs">{money(Number(fondo) || 0)} en el cajón</span>}
        </div>
      ) : <div className="text-xs">Usted no es cajero de la caja del taller: pida que abra el turno a un cajero o al administrador (Configuración → Caja y cobros).</div>}
    </div>
  );
}
